import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, Truck, Wallet, BarChart3, UserCog, Store, X, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/areas", label: "Areas", icon: MapPin, testid: "nav-areas" },
  { to: "/deliveries", label: "Deliveries", icon: Truck, testid: "nav-deliveries" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/staff", label: "Staff & Team", icon: UserCog, testid: "nav-staff", adminOnly: true },
];

function NavItems({ onNavigate }) {
  const { user } = useAuth();
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => {
        const Icon = n.icon;
        return (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-6 py-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
        <Store className="h-5 w-5" />
      </div>
      <div className="leading-tight">
        <div className="font-display text-lg font-extrabold tracking-tight">SARAVANA DELAPP</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Accounts &amp; Delivery</div>
      </div>
    </div>
  );
}

export default function AppSidebar({ mobileOpen, setMobileOpen }) {
  return (
    <>
      {/* Desktop */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card lg:flex">
        <Brand />
        <div className="mt-2 flex-1 overflow-y-auto pb-6">
          <NavItems />
        </div>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-card lg:hidden"
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
            >
              <div className="flex items-center justify-between">
                <Brand />
                <button className="pr-4 text-muted-foreground" onClick={() => setMobileOpen(false)}>
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-2 flex-1 overflow-y-auto pb-6">
                <NavItems onNavigate={() => setMobileOpen(false)} />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
